import React from 'react';

import { useScrollToLocationForReference } from 'src/editor';
import type { SelectedReference } from 'src/editor/toolbars/right-panel/ai/extraction/reports/ExtractionJobDetail';
import { openDocumentQuestion } from 'src/helpers';
import { useMaybeEditor } from 'src/layout-engine';

import type { MarkdownComponentProps } from '../../chat/ChatPanelMarkdown/ChatPanelMarkdown';
import { Citation } from '../../shared/components/Citation';
import { CitationCard } from '../../shared/components/CitationCard';

interface ExtractionQuestionCitationProps {
  documentId: string;
  /** Display name of the document these locations sit in. A blank name shows "Untitled Document". */
  documentTitle: string;
  questionId: string;
  reference: SelectedReference;
}

/**
 * Container component specifically for extraction job citations. This component
 * handles the business logic for extraction question citations, then renders
 * the presentational Citation component.
 */
export function ExtractionQuestionCitation({
  documentId,
  documentTitle,
  questionId,
  reference,
  ...rest
}: ExtractionQuestionCitationProps & MarkdownComponentProps<HTMLAnchorElement>): JSX.Element {
  const editor = useMaybeEditor();
  const { scrollToLocationForReference } = useScrollToLocationForReference();

  function openCitation() {
    if (!editor) {
      openDocumentQuestion({ documentId, questionId });
      return;
    }

    scrollToLocationForReference({ aiReference: reference });
  }

  const handleClickCitation = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    openCitation();
  };

  return (
    <Citation
      {...rest}
      card={
        <CitationCard
          jumpInPlace
          caption="This document"
          title={documentTitle}
          onViewInDocument={openCitation}
        />
      }
      tooltip="View citation"
      onClickCitation={handleClickCitation}
    />
  );
}
